import { __ } from '@wordpress/i18n';

const tocNav = document.querySelector('.plarium-toc');
const tocContent = document.querySelector('.plarium-toc-content');
const siteHeader = document.querySelector('.header');
const mediaQueryTablet = matchMedia('(max-width: 1023px)');

// Use this variable to only update scroll when the active item changes.
let lastActiveHash = '';

/**
 * Check if an element is in the viewport and not obscured by the site header.
 *
 * @param {HTMLElement} element The element to check.
 * @returns {boolean} Whether the element is in the viewport.
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  const thresholdTop = siteHeader.getBoundingClientRect().bottom;

  return (
    rect.top >= thresholdTop &&
    rect.left >= thresholdTop &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Check if an element is within the threshold of the site header.
 *
 * @param {HTMLElement} element The element to check.
 * @returns {boolean} Whether the element is within the threshold.
 */
function isInThreshold(element) {
  const rect = element.getBoundingClientRect();
  const thresholdTop = siteHeader.getBoundingClientRect().bottom;
  const thresholdBottom = thresholdTop + 30;

  return rect.top <= thresholdBottom;
}

/**
 * Get the heading to highlight in the table of contents.
 *
 * Returns
 *   - The first visible heading in the viewport unless it’s past the threshold,
 *   - in which case it returns the previous heading if available.
 *
 * @param {HTMLElement} element The container with headings.
 * @returns {HTMLElement|false} The heading element to highlight or false.
 */
function headingToHighlight(element) {
  const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');

  let headingIndex = 0;
  const firstHeadingInViewport = Array.from(headings).find((heading, index) => {
    if (isInViewport(heading)) {
      headingIndex = index;
      return true;
    }
    return false;
  });

  if (!firstHeadingInViewport) {
    return false;
  }

  // If the heading is below the threshold, use the previous heading if available.
  if (!isInThreshold(firstHeadingInViewport)) {
    if (headings[headingIndex - 1]) {
      return headings[headingIndex - 1];
    }
  }

  return firstHeadingInViewport;
}

/**
 * Update the table of contents based on the current scroll position.
 *
 * After updating the active class on the corresponding link, this function
 * scrolls only the toc container (.plarium-toc__list) so that the active link
 * is positioned at the top.
 */
function updateToc() {
  const heading = headingToHighlight(tocContent);
  const tableOfContentsLinks = tocNav.querySelectorAll('a');

  if (!heading || !tableOfContentsLinks) {
    lastActiveHash = '';
    return;
  }

  let activeLink = null;

  // Set the active class on the matching link.
  tableOfContentsLinks.forEach((link) => {
    const linkHash = decodeURIComponent(link.hash.slice(1));
    if (heading.id === linkHash) {
      link.classList.add('plarium-toc__list__item__link--active');
      link
        ?.closest('.plarium-toc__list')
        ?.closest('.plarium-toc__list__item')
        ?.classList.add('plarium-toc__list__item--submenu-open');
      activeLink = link;
    } else {
      link.classList.remove('plarium-toc__list__item__link--active');
    }
  });

  // If we found an active link and it has changed since the last update…
  if (activeLink) {
    if (activeLink.hash !== lastActiveHash) {
      lastActiveHash = activeLink.hash;

      // Identify the scrollable toc container.
      const tocList = tocNav.querySelector('.plarium-toc__list');
      if (tocList) {
        // Calculate desired scroll top based on the container’s and active link’s bounding boxes.
        const containerRect = tocList.getBoundingClientRect();
        const activeRect = activeLink.getBoundingClientRect();
        const desiredScrollTop = tocList.scrollTop + (activeRect.top - containerRect.top);

        // Only scroll if the difference is significant (to prevent jitter).
        if (Math.abs(tocList.scrollTop - desiredScrollTop) > 10) {
          tocList.scrollTo({
            top: desiredScrollTop,
            behavior: 'smooth',
          });
        }
      }
    }
  } else {
    lastActiveHash = '';
  }
}

/**
 * Dynamically add toggle buttons to any list items that have submenus.
 */
function addToggles(element) {
  const listItems = element.querySelectorAll('.plarium-toc__list__item');

  listItems?.forEach((listItem) => {
    const subMenu = listItem.querySelector('.plarium-toc__list');

    if (subMenu) {
      const listItemLink = listItem.querySelector('.plarium-toc__list__item__link');
      const opener = document.createElement('button');
      opener.classList.add('plarium-toc__toggle');
      opener.setAttribute('aria-label', __('Toggle submenu', 'plarium'));
      opener.setAttribute('aria-expanded', 'false');
      opener.setAttribute('aria-controls', subMenu.id);
      listItemLink.appendChild(opener);
      listItem.classList.add('plarium-toc__list__item--has-toggle');

      opener.addEventListener('click', () => {
        const expanded = opener.getAttribute('aria-expanded') === 'true';
        opener.setAttribute('aria-expanded', !expanded);
        listItem.classList.toggle('plarium-toc__list__item--submenu-open');
      });
    }
  });
}

/**
 * Add event listeners to enable smooth scrolling when a TOC link is clicked.
 */
function addSmoothScroll(element) {
  const tableOfContentsLinks = element.querySelectorAll('a');

  tableOfContentsLinks?.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();

      if (event.target.tagName !== 'A') {
        return;
      }

      const url = new URL(link.href);
      const targetId = decodeURIComponent(url.hash.slice(1));
      const target = document.getElementById(targetId);
      const offset = siteHeader.getBoundingClientRect().bottom + 10;
      const y = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    });
  });
}

/**
 * Add event listeners to close the menu when an item is clicked.
 * This applies only to the 'post' post type on mobile devices.
 *
 * @param {HTMLElement} element The table of contents element.
 */
function mobilePostToc(element) {
  const body = document.querySelector('body');
  if (!body.classList.contains('single-post')) {
    return;
  }

  const tableOfContentsLinks = element.querySelectorAll('a');
  const tableOfContentsToggle = element.querySelector('#plarium-toc__main-toggle');

  if (!tableOfContentsLinks || !tableOfContentsToggle) {
    return;
  }

  body.classList.add('has-toc');

  tableOfContentsLinks?.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (!mediaQueryTablet.matches) {
        return;
      }

      event.preventDefault();

      if (event.target.tagName !== 'A') {
        return;
      }

      const collapsed = tableOfContentsToggle.getAttribute('aria-expanded') === 'false';
      tableOfContentsToggle.setAttribute('aria-expanded', !collapsed);
      element.classList.toggle('plarium-toc--open');
    });
  });
}

/**
 * Add event handlers to the table of contents.
 *
 * @param {HTMLElement} element The table of contents element.
 */
function addEventHandlers(element) {
  const tableOfContentsToggle = element.querySelector('#plarium-toc__main-toggle');

  tableOfContentsToggle?.addEventListener('click', () => {
    const expanded = tableOfContentsToggle.getAttribute('aria-expanded') === 'true';
    tableOfContentsToggle.setAttribute('aria-expanded', !expanded);
    element.classList.toggle('plarium-toc--open');
  });
}

window.addEventListener('DOMContentLoaded', () => {
  if (tocNav && tocContent) {
    updateToc();

    // Attach (or remove) the scroll event listener based on tocContent visibility.
    const tocObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          document.addEventListener('scroll', updateToc);
        } else {
          document.removeEventListener('scroll', updateToc);
          updateToc();
        }
      });
    });
    tocObserver.observe(tocContent);

    addToggles(tocNav);
    addSmoothScroll(tocNav);
    addEventHandlers(tocNav);
    mobilePostToc(tocNav);
  }
});
